<template>
  <b-card>
    <slot name="hint" />
    <b-card-title class="mb-0">
      Future Maturities
    </b-card-title>
    <div class="font-small-3 mb-1 text-muted">
      Breakdown of possible and final future maturities
    </div>
    <b-card-text>
      <div v-if="loading">
        <loading-container />
      </div>
      <div
        v-else
        class="position-relative"
      >
        <div
          v-if="noData"
          class="no-data-overlay"
        >
          <span class="text-muted">No data available</span>
        </div>
        <app-echart-bar
          ref="chart"
          :option-data="chartData"
          :style="{ height: compact ? '200px' : '420px' }"
        />
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import { BCard, BCardTitle, BCardText } from 'bootstrap-vue'
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from '../../breakdown/charts/offscreenRender'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardText,
    AppEchartBar,
    LoadingContainer,
  },
  props: {
    futureMaturitiesData: {
      type: Object,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
    futureMaturities() {
      return this.futureMaturitiesData?.futurematurities ?? null
    },
    noData() {
      return !Array.isArray(this.futureMaturities?.series) || !this.futureMaturities.series.some(s => (
        Array.isArray(s.data) && s.data.some(v => v !== 0)
      ))
    },
    chartData() {
      const fm = this.futureMaturities
      if (!fm || !Array.isArray(fm.series)) {
        return {
          legend: {}, grid: {}, xAxis: {}, yAxis: {}, series: [],
        }
      }

      const colors = ['#ea5455', '#00cfe8']

      const series = fm.series.map((s, index) => {
        const config = {
          name: s.name,
          type: 'bar',
          data: Array.isArray(s.data) ? s.data : [],
          barWidth: this.compact ? 10 : '25%',
          itemStyle: { color: colors[index % colors.length] },
          emphasis: { itemStyle: { color: colors[index % colors.length] } },
        }
        if (this.compact) {
          config.barGap = '5%'
          config.barCategoryGap = '30%'
        }
        return config
      })

      return {
        legend: { show: true, left: 0 },
        grid: this.compact ? {
          left: 10, right: 10, bottom: 50, top: 35, containLabel: true,
        } : {
          left: 70, right: 30, bottom: 70, top: 60, containLabel: false,
        },
        xAxis: {
          type: 'category',
          name: 'Time Period (Half-Yearly)',
          nameLocation: 'middle',
          nameGap: 35,
          data: Array.isArray(fm.categories) ? fm.categories : [],
          axisLabel: { fontSize: 12 },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          name: 'Product Count',
          nameLocation: 'middle',
          nameGap: 45,
          nameTextStyle: { fontSize: 13, fontWeight: 500 },
          axisLabel: { fontSize: 12 },
          splitLine: {
            lineStyle: { opacity: this.splitLineOpacity, type: 'dashed' },
          },
        },
        series,
      }
    },
  },
  methods: {
    getDataURL() {
      if (this.loading) return null
      return this.noData ? null : renderChartToBase64(this.chartData)
    },
    resizeChart() {
      if (this.$refs.chart) {
        this.$refs.chart.resize()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.no-data-overlay {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  font-size: 1.1rem;
}
</style>
